/**
 * Amazon Reviews Widget — Standalone (Storefront API edition)
 *
 * Fully self-contained. Reads amazon_reviews metafields via Shopify Storefront API.
 * Injected via ScriptTag API (no theme.liquid dependency).
 *
 * Handles two contexts:
 *   1. Product page (/products/*) — mini badge near title + full review section
 *   2. Collection / home page — mini star badges on each product card
 */
(function () {
  'use strict';

  var STOREFRONT_TOKEN = '78c313377f6c240815b19d9a68760f87';
  var SHOP_DOMAIN      = 'ecbrandz.myshopify.com';
  var MAX_REVIEWS      = 8;
  var TRUNCATE_WORDS   = 60;

  // ── helpers ──────────────────────────────────────────────────────────────

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function truncate(str, words) {
    var parts = str.trim().split(/\s+/);
    return parts.length <= words ? str : parts.slice(0, words).join(' ') + '…';
  }

  function formatDate(iso) {
    if (!iso) return '';
    try {
      return new Date(iso + 'T00:00:00')
        .toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) { return iso; }
  }

  /**
   * Render 5 stars with proper half-star using CSS clip.
   * cls = base CSS class name (default 'amzr-star')
   */
  function stars(rating, cls) {
    cls = cls || 'amzr-star';
    var full  = Math.floor(rating);
    var half  = (rating - full) >= 0.2 ? 1 : 0;
    var empty = 5 - full - half;
    var html  = '';
    for (var i = 0; i < full; i++)
      html += '<span class="' + cls + ' ' + cls + '--full">&#9733;</span>';
    if (half)
      html += '<span class="' + cls + ' ' + cls + '--half-star">&#9733;</span>';
    for (var j = 0; j < empty; j++)
      html += '<span class="' + cls + ' ' + cls + '--empty">&#9733;</span>';
    return html;
  }

  // ── inject shared CSS once ────────────────────────────────────────────

  var stylesInjected = false;
  function injectStyles() {
    if (stylesInjected) return;
    stylesInjected = true;
    var s = document.createElement('style');
    s.textContent = [
      /* ── stars ── */
      '.amzr-star{font-size:17px;line-height:1;display:inline-block}',
      '.amzr-star--full{color:#D4892A}',
      '.amzr-star--empty{color:#d4d0cc}',
      '.amzr-star--half-star{background:linear-gradient(to right,#D4892A 50%,#d4d0cc 50%);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}',

      /* ── card mini badge (collection / home page) ── */
      '.amzr-card-badge{display:flex;align-items:center;gap:5px;margin:3px 0 6px;flex-wrap:wrap}',
      '.amzr-card-badge .amzr-star{font-size:13px}',
      '.amzr-card-score{font-size:12px;font-weight:600;color:#1c1c1e}',
      '.amzr-card-count{font-size:11px;color:#888}',

      /* ── product page: mini badge near price ── */
      '.amzr-mini{display:flex;align-items:center;gap:7px;margin:4px 0 10px;flex-wrap:wrap}',
      '.amzr-mini .amzr-star{font-size:15px}',
      '.amzr-mini-score{font-size:14px;font-weight:600;color:#1c1c1e}',
      '.amzr-mini-count{font-size:13px;color:#6e6e6e}',
      '.amzr-mini-src{font-size:11px;color:#aaa}',

      /* ── product page: full review section ── */
      '.amzr-section{max-width:1200px;margin:48px auto 0;padding:0 20px 60px;font-family:inherit}',
      '.amzr-header{margin-bottom:32px;border-bottom:1px solid #e8e4df;padding-bottom:20px}',
      '.amzr-title{font-size:22px;font-weight:600;color:#1c1c1e;margin:0 0 12px}',
      '.amzr-agg{display:flex;align-items:center;gap:12px;flex-wrap:wrap}',
      '.amzr-agg-rating{font-size:28px;font-weight:700;color:#1c1c1e}',
      '.amzr-agg-stars{display:inline-flex;gap:2px;align-items:center}',
      '.amzr-agg .amzr-star{font-size:22px}',
      '.amzr-count{font-size:14px;color:#6e6e6e}',
      '.amzr-source{font-size:12px;color:#9e9e9e;margin-left:2px}',
      '.amzr-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:20px}',
      '.amzr-review-card{background:#FAF8F5;padding:24px;display:flex;flex-direction:column;gap:10px}',
      '.amzr-review-card-stars{display:flex;gap:2px}',
      '.amzr-review-card-title{font-size:15px;font-weight:600;color:#1c1c1e;line-height:1.3}',
      '.amzr-review-card-body{font-size:14px;color:#444;line-height:1.6}',
      '.amzr-review-card-meta{font-size:12px;color:#9e9e9e;display:flex;flex-wrap:wrap;gap:6px;align-items:center}',
      '.amzr-badge{display:inline-flex;align-items:center;gap:3px;font-size:11px;color:#008a00;border:1px solid #c0e0c0;padding:1px 6px;border-radius:2px}',
      '@media(max-width:600px){.amzr-grid{grid-template-columns:1fr}}'
    ].join('');
    document.head.appendChild(s);
  }

  // ── Storefront API call ───────────────────────────────────────────────

  function storefrontQuery(query, callback) {
    var xhr = new XMLHttpRequest();
    xhr.open('POST', 'https://' + SHOP_DOMAIN + '/api/2024-10/graphql.json', true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('X-Shopify-Storefront-Access-Token', STOREFRONT_TOKEN);
    xhr.onload = function () {
      if (xhr.status !== 200) return;
      try { callback(JSON.parse(xhr.responseText)); } catch (e) {}
    };
    xhr.send(JSON.stringify({ query: query }));
  }

  // ── extract product handle from any Shopify product URL ──────────────

  function handleFromHref(href) {
    var m = href.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  // ========================================================================
  // CONTEXT 1: Product page — full widget + mini badge
  // ========================================================================

  var path = window.location.pathname;
  if (path.match(/\/products\/([^/?#]+)/)) {
    var productHandle = path.match(/\/products\/([^/?#]+)/)[1];

    storefrontQuery(
      '{ product(handle: "' + productHandle + '") {' +
        'metaSummary: metafield(namespace: "amazon_reviews", key: "summary") { value }' +
        'metaReviews: metafield(namespace: "amazon_reviews", key: "reviews") { value }' +
      '} }',
      function (data) {
        var p = data && data.data && data.data.product;
        if (!p || !p.metaSummary) return;
        var summary = JSON.parse(p.metaSummary.value);
        var reviews = p.metaReviews ? JSON.parse(p.metaReviews.value) : [];
        if (!summary || summary.count === 0) return;
        injectStyles();
        injectProductMini(summary);
        injectFullReviews(summary, reviews);
      }
    );
  }

  // ── mini badge near product title (product page) ───────────────────────

  function injectProductMini(summary) {
    var titleEl = document.querySelector('.product-single__title')
      || document.querySelector('h1.product-single__title')
      || document.querySelector('[class*="product-single"] h1');
    var priceEl = document.querySelector('[id^="ProductPrice"]')
      || document.querySelector('.product-single__price');
    if (!titleEl && !priceEl) return;

    var mini = document.createElement('div');
    mini.className = 'amzr-mini';
    mini.innerHTML =
      '<span>' + stars(summary.rating) + '</span>' +
      '<span class="amzr-mini-score">' + summary.rating.toFixed(1) + '</span>' +
      '<span class="amzr-mini-count">' + Number(summary.count).toLocaleString() + ' ratings</span>' +
      '<span class="amzr-mini-src">on Amazon</span>';

    if (titleEl) titleEl.parentNode.insertBefore(mini, titleEl.nextSibling);
    else priceEl.parentNode.insertBefore(mini, priceEl);
  }

  // ── full review section (product page) ────────────────────────────────

  function injectFullReviews(summary, reviews) {
    var anchor = document.querySelector('.product-single__description')
      || document.querySelector('.product-single')
      || document.querySelector('[class*="product"]');
    if (!anchor) return;

    var shown = reviews.slice(0, MAX_REVIEWS);
    var cards = shown.map(function (r) {
      var verified = r.verified_purchase
        ? '<span class="amzr-badge">&#10003; Verified Purchase</span>' : '';
      return '<div class="amzr-review-card">'
        + '<div class="amzr-review-card-stars">' + stars(r.rating) + '</div>'
        + (r.title ? '<div class="amzr-review-card-title">' + escHtml(r.title) + '</div>' : '')
        + (r.body  ? '<div class="amzr-review-card-body">'  + escHtml(truncate(r.body, TRUNCATE_WORDS)) + '</div>' : '')
        + '<div class="amzr-review-card-meta">'
        + (r.author ? '<span>' + escHtml(r.author) + '</span>' : '')
        + (r.date   ? '<span>&middot; ' + escHtml(formatDate(r.date)) + '</span>' : '')
        + verified + '</div></div>';
    }).join('');

    var html = '<div class="amzr-section">'
      + '<div class="amzr-header">'
      + '<h2 class="amzr-title">Customer Reviews</h2>'
      + '<div class="amzr-agg">'
      + '<span class="amzr-agg-rating">' + summary.rating.toFixed(1) + '</span>'
      + '<span class="amzr-agg-stars">' + stars(summary.rating) + '</span>'
      + '<span class="amzr-count">' + Number(summary.count).toLocaleString() + ' ratings</span>'
      + '<span class="amzr-source">on Amazon</span>'
      + '</div></div>'
      + '<div class="amzr-grid">' + cards + '</div>'
      + '</div>';

    var wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    anchor.parentNode.insertBefore(wrapper, anchor.nextSibling);
  }

  // ========================================================================
  // CONTEXT 2: Collection / home page — mini star badges on product cards
  // ========================================================================

  // Run after DOM is ready (cards may be rendered after DOMContentLoaded)
  function runCollectionBadges() {
    var cards = document.querySelectorAll('.product-card');
    if (!cards || cards.length === 0) return;

    // Collect unique handles and map to their card elements
    var handleToCards = {};
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      // Find the product link within the card
      var linkEl = card.querySelector('.full-width-link')
        || card.querySelector('[href*="/products/"]');
      if (!linkEl) continue;
      var handle = handleFromHref(linkEl.getAttribute('href'));
      if (!handle) continue;
      if (!handleToCards[handle]) handleToCards[handle] = [];
      handleToCards[handle].push(card);
    }

    var handles = Object.keys(handleToCards);
    if (handles.length === 0) return;

    // Build a single batched GraphQL query with aliases
    var fields = handles.map(function (h, idx) {
      return 'p' + idx + ': product(handle: "' + h + '") { '
        + 'metafield(namespace: "amazon_reviews", key: "summary") { value } }';
    }).join(' ');

    storefrontQuery('{ ' + fields + ' }', function (data) {
      if (!data || !data.data) return;
      injectStyles();

      handles.forEach(function (handle, idx) {
        var productData = data.data['p' + idx];
        if (!productData || !productData.metafield) return;
        var summary;
        try { summary = JSON.parse(productData.metafield.value); } catch (e) { return; }
        if (!summary || summary.count === 0) return;

        var cardList = handleToCards[handle];
        cardList.forEach(function (card) {
          // Skip if badge already injected (prevents double-run if script loads twice)
          if (card.querySelector('.amzr-card-badge')) return;

          // Find title element to inject after
          var titleEl = card.querySelector('.grid-view-item__title')
            || card.querySelector('.product-card__title')
            || card.querySelector('[class*="product-card__title"]');
          if (!titleEl) return;

          var badge = document.createElement('div');
          badge.className = 'amzr-card-badge';
          badge.innerHTML =
            '<span>' + stars(summary.rating, 'amzr-star') + '</span>' +
            '<span class="amzr-card-score">' + summary.rating.toFixed(1) + '</span>' +
            '<span class="amzr-card-count">(' + Number(summary.count).toLocaleString() + ')</span>';

          titleEl.parentNode.insertBefore(badge, titleEl.nextSibling);
        });
      });
    });
  }

  // Trigger collection badges after page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runCollectionBadges);
  } else {
    runCollectionBadges();
  }

})();
